import { LinearLoader } from '@advanza/ui'
import {
    TrustCheckCheckSummary,
    TrustCheckSummary,
} from '@trustoo/types/api/controllers/trustCheckReport'
import clsx from 'clsx'
import {
    CHECK_TYPES,
    checkStatusLabel,
    formatRate,
    outcomeLabel,
} from 'components/reports/trustCheck/trustCheckLabels'
import { useAppSelector } from 'hooks/hooks'
import { ReactNode } from 'react'

const Card = ({
    title,
    children,
    className,
}: {
    title: string
    children: ReactNode
    className?: string
}) => (
    <div
        className={clsx(
            'min-w-[320px] flex-1 rounded-lg border border-grey-200 bg-white p-4',
            className,
        )}>
        <h4 className="mb-3 text-md font-bold text-grey-900">{title}</h4>
        {children}
    </div>
)

const Table = ({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) => (
    <table className="w-full text-md">
        <thead>
            <tr className="text-left text-grey-700">
                {headers.map((header) => (
                    <th key={header} className="py-1 pr-2 font-medium">
                        {header}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>
            {rows.length === 0 ? (
                <tr>
                    <td className="py-1 text-grey-700" colSpan={headers.length}>
                        No data
                    </td>
                </tr>
            ) : (
                rows.map((cells, index) => (
                    <tr key={index} className="border-t border-grey-100">
                        {cells.map((cell, cellIndex) => (
                            <td key={cellIndex} className="py-1 pr-2 align-top">
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))
            )}
        </tbody>
    </table>
)

const percentage = (part: number, total: number): string =>
    total > 0 ? `${Math.round((part / total) * 1000) / 10}%` : '-'

const MarketsCard = ({ summary }: { summary: TrustCheckSummary }) => (
    <Card title="Outcome per market">
        <Table
            headers={[
                'Market',
                'Providers',
                'Approved',
                'Approved (low coverage)',
                'Under review',
                'Blacklisted',
                'Known bad actors',
            ]}
            rows={summary.by_market.map((row) => [
                <strong key="market">{row.country_code}</strong>,
                row.total,
                `${row.approved} (${percentage(row.approved, row.total)})`,
                `${row.approved_low_coverage} (${percentage(row.approved_low_coverage, row.total)})`,
                `${row.under_review} (${percentage(row.under_review, row.total)})`,
                `${row.blacklisted} (${percentage(row.blacklisted, row.total)})`,
                `${row.bad_actors} (${row.currently_banned} banned)`,
            ])}
        />
    </Card>
)

const ChecksCard = ({ summary }: { summary: TrustCheckSummary }) => {
    const byType = CHECK_TYPES.map((type) => ({
        type,
        rows: summary.by_check.filter((row) => row.check_type === type.value),
    })).filter((group) => group.rows.length > 0)

    const cells = (row: TrustCheckCheckSummary) => [
        row.country_code,
        row.total,
        row.pass,
        row.report_only,
        row.under_review,
        row.blacklisted,
        `${row.not_checkable} (${percentage(row.not_checkable, row.total)})`,
    ]

    return (
        <Card title="Result per check and market">
            <Table
                headers={[
                    'Check',
                    'Market',
                    'Providers',
                    'Pass',
                    'Report only',
                    'Under review',
                    'Blacklisted',
                    'Not checkable',
                ]}
                rows={byType.flatMap((group) =>
                    group.rows.map((row, index) => [
                        index === 0 ? <strong key="check">{group.type.name}</strong> : '',
                        ...cells(row),
                    ]),
                )}
            />
        </Card>
    )
}

const SubSignalsCard = ({ summary }: { summary: TrustCheckSummary }) => (
    <Card title="Sub-signals per market">
        <p className="mb-2 text-sm text-grey-700">
            Providers each sub-signal fired on. &quot;Bad actors&quot; are banned or (NL) churned
            for bad debt / insufficient quality; &quot;live clean&quot; are live providers without
            that label.
        </p>
        <Table
            headers={[
                'Check',
                'Sub-signal',
                'Severity',
                'Market',
                'Providers',
                'Bad actors',
                'Live clean',
            ]}
            rows={summary.by_sub_signal.map((row) => [
                CHECK_TYPES.find((type) => type.value === row.check_type)?.short ?? row.check_type,
                <code key="signal">{row.sub_signal}</code>,
                checkStatusLabel(row.severity),
                row.country_code,
                row.providers,
                row.bad_actors,
                row.live_clean,
            ])}
        />
    </Card>
)

const ValidationCard = ({ summary }: { summary: TrustCheckSummary }) => (
    <Card title="Validation: catch rate and false positives">
        <p className="mb-2 text-sm text-grey-700">
            Catch rate: known bad actors that would have ended under review or blacklisted.
            False-positive rate: live clean providers that would have been flagged. Report-only
            sub-signals get the same rates so they can be promoted to real rules.
        </p>
        <Table
            headers={[
                'Market',
                'Bad actors',
                'Caught',
                'Catch rate',
                'Live clean',
                'Flagged',
                'False-positive rate',
            ]}
            rows={summary.validation.flatMap((row) => [
                [
                    <strong key="market">{row.country_code}</strong>,
                    row.bad_actors,
                    row.bad_actors_flagged,
                    <strong key="catch">{formatRate(row.catch_rate)}</strong>,
                    row.clean,
                    row.clean_flagged,
                    <strong key="fp">{formatRate(row.false_positive_rate)}</strong>,
                ],
                ...row.report_only.map((signal) => [
                    <span key="signal" className="pl-4 text-grey-700">
                        ↳ <code>{signal.sub_signal}</code>
                    </span>,
                    '',
                    signal.bad_actors_hit,
                    formatRate(signal.catch_rate),
                    '',
                    signal.clean_hit,
                    formatRate(signal.false_positive_rate),
                ]),
            ])}
        />
    </Card>
)

const WeeksCard = ({ summary }: { summary: TrustCheckSummary }) => {
    const weeks = Array.from(new Set(summary.under_review_by_week.map((row) => row.week))).sort()
    const markets = Array.from(
        new Set(summary.under_review_by_week.map((row) => row.country_code)),
    ).sort()
    const countFor = (week: string, market: string) =>
        summary.under_review_by_week.find((row) => row.week === week && row.country_code === market)
            ?.under_review ?? 0

    return (
        <Card title="Under review per sign-up week" className="max-h-[420px] overflow-auto">
            <Table
                headers={['Week', ...markets, 'Total']}
                rows={weeks.map((week) => {
                    const counts = markets.map((market) => countFor(week, market))
                    return [week, ...counts, counts.reduce((sum, count) => sum + count, 0)]
                })}
            />
        </Card>
    )
}

const CategoriesCard = ({ summary }: { summary: TrustCheckSummary }) => (
    <Card title="Under review per high-risk category">
        <Table
            headers={['Market', 'Service', 'Under review']}
            rows={summary.under_review_by_category.map((row) => [
                row.country_code,
                `${row.service_name ?? 'Unknown'} (#${row.service_id})`,
                row.under_review,
            ])}
        />
    </Card>
)

const IpOnlyCard = ({ summary }: { summary: TrustCheckSummary }) => (
    <Card title="IP-only matches: accounts sharing the IP">
        <p className="mb-2 text-sm text-grey-700">
            Providers flagged on IP alone (no device match), by how many other accounts share that
            IP. Helps decide the threshold for an IP-only rule.
        </p>
        <Table
            headers={['Accounts sharing IP', 'Providers']}
            rows={summary.ip_only_shared_accounts.map((row) => [
                row.accounts_sharing_ip,
                row.providers,
            ])}
        />
    </Card>
)

const TrustCheckReportSummary = () => {
    const { summary, summaryLoading, run } = useAppSelector((state) => state.trustCheckReport)

    if (summaryLoading && summary === null) {
        return (
            <div className="py-6 text-center">
                <LinearLoader />
            </div>
        )
    }

    if (summary === null || run === null) {
        return null
    }

    const totals = summary.by_market.reduce(
        (acc, row) => ({
            total: acc.total + row.total,
            under_review: acc.under_review + row.under_review,
            blacklisted: acc.blacklisted + row.blacklisted,
        }),
        { total: 0, under_review: 0, blacklisted: 0 },
    )

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-6 text-md text-grey-800">
                <span>
                    <strong>Providers:</strong> {totals.total}
                </span>
                <span>
                    <strong>{outcomeLabel('under_review')}:</strong> {totals.under_review} (
                    {percentage(totals.under_review, totals.total)})
                </span>
                <span>
                    <strong>{outcomeLabel('blacklisted')}:</strong> {totals.blacklisted} (
                    {percentage(totals.blacklisted, totals.total)})
                </span>
            </div>
            <div className="flex flex-wrap gap-4">
                <MarketsCard summary={summary} />
                <ValidationCard summary={summary} />
            </div>
            <div className="flex flex-wrap gap-4">
                <ChecksCard summary={summary} />
                <SubSignalsCard summary={summary} />
            </div>
            <div className="flex flex-wrap gap-4">
                <WeeksCard summary={summary} />
                <CategoriesCard summary={summary} />
                <IpOnlyCard summary={summary} />
            </div>
        </div>
    )
}

export default TrustCheckReportSummary
